import {Button, view, TextField, Spinner} from '@sail/ui';
import {
  DefaultTheme,
  LightCard,
  LinkAssistantTheme,
  ThemeProvider,
  useTheme,
} from '@stripe-internal/conversations-ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {useCallback, useEffect, useState, useRef} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

const messages = defineMessages({
  formTitle: {
    id: 'mse_inline_data.alo_auth.email_input_form.formTitle',
    description: 'The title of the email input form',
    defaultMessage: 'Enter email address',
  },
  linkFormTitle: {
    id: 'mse_inline_data.alo_auth.email_input_form.linkFormTitle',
    description: 'The title of the email input form',
    defaultMessage: 'Account email',
  },
  fieldPlaceholderEmail: {
    id: 'mse_inline_data.alo_auth.email_input_form.fieldPlaceholderEmail',
    description: 'The placeholder for the email input field',
    defaultMessage: 'Email',
  },
  submitText: {
    id: 'mse_inline_data.alo_auth.email_input_form.submitText',
    description: 'Text on the submit button',
    defaultMessage: 'Submit',
  },
});

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const validateEmail = (email: string): boolean => {
  const trimmedEmail = email.trim();
  return trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
};

export type ValueProps = {
  email: string;
  audience: string;
  errorMessage?: string;
};

const getThemeForAudience = (audienceType: string) => {
  switch (audienceType) {
    case 'stripe':
      return DefaultTheme;
    case 'link':
      return LinkAssistantTheme;
    case 'express':
      return DefaultTheme;
    default:
      return DefaultTheme;
  }
};

export const EmailInputForm = (props: InlineDatumReactComponentProps) => {
  const {onDone, isDone, datum} = props;

  const value = datum.value as ValueProps;
  const {email: initialEmail, audience, errorMessage} = value;

  const intl = useIntl();
  const analytics = useAnalytics();

  const [email, setEmail] = useState(initialEmail || '');
  const [previousEmail, setPreviousEmail] = useState(initialEmail || '');
  const [isEditing, setIsEditing] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isValid, setIsValid] = useState(false);
  const [userModified, setUserModified] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setIsEditing(!isDone);
    if (isDone) {
      setIsLoading(false);
    }
  }, [isDone]);

  useEffect(() => {
    if (initialEmail) {
      const isInitialValid = validateEmail(initialEmail);
      setIsValid(isInitialValid);
      setPreviousEmail(initialEmail);
    }
  }, [initialEmail]);

  const theme = useTheme();

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newEmail = e.target.value;
      setEmail(newEmail);

      const isEmailValid = validateEmail(newEmail);
      setIsValid(isEmailValid);

      if (newEmail !== previousEmail) {
        setUserModified(true);
      } else {
        setUserModified(false);
      }
    },
    [previousEmail],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const trimmedEmail = email.trim();

      if (!isValid || !validateEmail(trimmedEmail)) {
        return;
      }

      setIsEditing(false);
      setIsLoading(true);
      setPreviousEmail(trimmedEmail);

      onDone({
        type: 'success',
        message: 'Email Input Form Data',
        extra: {
          dataWithPii: {
            email: trimmedEmail,
          },
        },
      });

      analytics.action('email_input_form_submit_click', {
        email_length: trimmedEmail.length,
        email_domain: trimmedEmail.split('@')[1] || '',
      });
    },
    [email, isValid, onDone, analytics],
  );

  const getError = () => {
    if (!userModified && errorMessage) {
      return errorMessage;
    }

    return undefined;
  };

  if (!isEditing) {
    if (isLoading) {
      return (
        <view.div
          style={{margin: 20, display: 'flex', justifyContent: 'center'}}
        >
          <Spinner size="medium" />
        </view.div>
      );
    }
    return null;
  }

  const submitButtonContent = <FormattedMessage {...messages.submitText} />;

  const submitButton = (
    <Button
      css={{
        width: 'fill',
        padding: theme.submitButtonPadding,
        borderRadius: theme.submitButtonBorderRadius,
        backgroundColor: theme.primaryActionColor,
        color: theme.onActionPrimaryTextColor,
        keyline: theme.primaryActionColor,
        topShadow: theme.primaryActionShadow,
        '[aria-disabled="true"]::after': {
          backgroundColor: theme.primaryActionDisabledColor,
          color: theme.onActionPrimaryTextColor,
          opacity: theme.ctaDisabledOpacity,
          borderRadius: theme.submitButtonBorderRadius,
        },
        ':hover:not(:active):not([aria-disabled="true"])': {
          backgroundColor: theme.primaryActionColor,
          color: theme.onActionPrimaryTextColor,
          topShadow: theme.primaryActionShadowHovered,
          keyline: theme.primaryActionColorHoveredKeyline,
        },
        ':active:focus:not([aria-disabled="true"])': {
          backgroundColor: theme.primaryActionActiveColor,
          keyline: theme.primaryActionActiveColor,
          topShadow: theme.pressedShadow,
          color: theme.primaryActionActiveTextColor,
        },
        ':focus': {
          topShadow: theme.pressedShadow,
          backgroundColor: theme.primaryActionColor,
          keyline: theme.primaryActionColor,
          focusRing: theme.focusRing,
        },
      }}
      key="button"
      disabled={!isValid}
      data-testid="email-submit-btn"
      behavior="submit"
    >
      {submitButtonContent}
    </Button>
  );

  const textFieldSubviews = {
    label: {
      css: {
        marginBottom: theme.formTitleLabelMarginBottom,
        fontSize: theme.formTitleFontSize,
        fontWeight: theme.formTitleFontWeight,
        lineHeight: theme.formTitleLineHeight,
        color: theme.formTitleColor,
      },
    },
    ...(theme.textFieldBackgroundColor && {
      input: {
        css: {
          backgroundColor: isValid
            ? theme.textFieldFocusBackgroundColor
            : theme.textFieldBackgroundColor,
          border: isValid ? theme.textFieldFocusBorder : theme.textFieldBorder,
          borderColor: isValid ? theme.textFieldFocusBorderColor : undefined,
          boxShadow: theme.textFieldBoxShadow,
          borderRadius: theme.textFieldBorderRadius,
          padding: audience === 'link' ? '16px 16px' : undefined,
          '::placeholder': {
            color: theme.textFieldPlaceholderColor,
            fontSize: theme.textFieldPlaceholderFontSize,
          },
          ':focus': {
            border: theme.textFieldFocusBorder,
            borderColor: theme.textFieldFocusBorderColor,
            backgroundColor: theme.textFieldFocusBackgroundColor,
          },
        },
      },
    }),
  };

  const formTitleMessage =
    audience === 'link' ? messages.linkFormTitle : messages.formTitle;

  return (
    <ThemeProvider value={getThemeForAudience(audience)}>
      <view.div css={{marginTop: 'small', width: 'fill'}}>
        <LightCard>
          <view.form css={{gap: 'medium', stack: 'y'}} onSubmit={handleSubmit}>
            <view.div css={{width: 'fill'}}>
              <TextField
                id="email-field"
                ref={inputRef}
                type="email"
                label={intl.formatMessage(formTitleMessage)}
                placeholder={intl.formatMessage(messages.fieldPlaceholderEmail)}
                width="maximized"
                value={email}
                onChange={handleEmailChange}
                autoComplete="email"
                data-testid="email-input"
                error={getError()}
                subviews={textFieldSubviews}
              />
            </view.div>

            {submitButton}
          </view.form>
        </LightCard>
      </view.div>
    </ThemeProvider>
  );
};
